import { QrCode } from "./qr";
import { SheetLabel, SheetPage, SheetSpacer } from "./sheet-chrome";
import type { SheetData } from "./sheet-data";

/**
 * Page 1. Its one job is: scan this.
 *
 * The QR is the largest, highest-contrast thing on the page, and the access code
 * sits below an "or" rule rather than beside it. That hierarchy is the product's:
 * scanning is the whole sign-in, and typing six digits is the fallback for a
 * second device or a household without the sheet to hand. Presenting the two as
 * equals would misrepresent the flow.
 */
export function SheetFront({ sheet }: { sheet: SheetData }) {
  return (
    <SheetPage kind="Start here">
      <h1 className="text-primary mt-[9mm] text-[34pt] leading-[1.08] tracking-[-0.5pt]">
        {sheet.headline.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </h1>

      <p className="text-muted mt-[5mm] max-w-[118mm] text-[12pt] leading-[1.55]">
        Everything is already configured for your home. You will be connecting the hardware, one
        compartment of your box at a time. {capitalise(sheet.totalTimeWords)} in total.
      </p>

      <div className="bg-card mt-[10mm] flex items-center gap-[11mm] rounded-[3mm] px-[14mm] py-[13mm]">
        {sheet.qrUrl ? (
          <QrCode
            value={sheet.qrUrl}
            title="Scan to open your home's setup"
            className="text-primary size-[54mm] shrink-0"
          />
        ) : (
          <div className="border-border text-muted grid size-[54mm] shrink-0 place-items-center rounded-[2mm] border-[0.4mm] border-dashed p-[6mm] text-center text-[10pt]">
            This home has no access code yet, so there is nothing to scan.
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="text-primary text-[16pt] leading-[1.25] font-bold">
            Scan with your phone camera.
          </p>
          <p className="text-muted mt-[2mm] text-[10pt] leading-[1.45]">
            It opens your home&rsquo;s setup. Nothing to type, nothing to install.
          </p>

          <div className="text-faint mt-[7mm] mb-[5mm] flex items-center gap-[3mm] text-[8.5pt] tracking-[0.8pt] uppercase before:h-[0.25mm] before:flex-1 before:bg-current after:h-[0.25mm] after:flex-1 after:bg-current">
            or
          </div>

          <p className="text-muted text-[10pt] leading-[1.5]">
            Open <b className="text-primary font-bold">{sheet.domain}</b>
            <br />
            and enter the code below.
          </p>
          <div className="bg-surface-subtle mt-[3.5mm] flex items-center justify-between gap-[4mm] rounded-[2mm] px-[5mm] py-[4mm]">
            <span className="text-muted text-[8pt] tracking-[0.8pt] whitespace-nowrap uppercase">
              Access code
            </span>
            <span className="text-primary text-[21pt] font-bold tracking-[3.2pt] whitespace-nowrap tabular-nums">
              {sheet.codeDisplay || "——————"}
            </span>
          </div>
        </div>
      </div>

      <p className="text-muted mt-[9mm] text-center text-[9.5pt]">
        Turn over for <b className="text-primary font-bold">what&rsquo;s in your box</b>.
      </p>

      <SheetSpacer />

      <footer className="border-border flex items-end justify-between gap-[8mm] border-t-[0.25mm] pt-[5mm]">
        <div>
          <SheetLabel>This kit belongs to</SheetLabel>
          <div className="text-primary mt-[1.5mm] text-[11pt] font-bold">{sheet.belongsTo}</div>
          {sheet.belongsSub && (
            <div className="text-muted mt-[0.8mm] text-[10pt]">{sheet.belongsSub}</div>
          )}
        </div>
        <div className="text-muted text-right text-[9pt] leading-[1.5]">
          Keep this sheet with the box.
          <br />
          The code works for this home only.
        </div>
      </footer>
    </SheetPage>
  );
}

/** "about one hour" → "About one hour", for the start of a sentence. */
function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
